import { getSortedGoals, useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { lifeAreaOtherID } from 'shared/types/orderable-section';
import { GoalSortingMode } from 'shared/types/sorting-mode';
import { sortItems } from 'shared/utils/sort-items';

import { GoalsFilterOptions, goalsFilters } from '../utils/goals-filter';

export const useLifeAreaActiveGoalSections = () => {
  const today = useToday();
  const user = useUser();
  const lifeAreas = useLocalizedLifeAreas();
  const goals = useGoals();

  const activeGoals = useMemo(
    () =>
      goalsFilters[GoalsFilterOptions.Active](goals, today).filter(
        ({ parentIds }) => !parentIds?.length,
      ),
    [goals, today],
  );

  const sections = useMemo(
    () =>
      [
        ...sortItems(
          lifeAreas,
          user?.settings?.lifeAreaSorting ?? [],
          'id',
        ).map(({ id }) => ({
          id,
          items: getSortedGoals(
            activeGoals.filter(({ lifeAreaId }) => lifeAreaId === id),
            {
              mode: [user?.settings?.goalSortingMode].filter(
                Boolean,
              ) as GoalSortingMode[],
              order:
                user?.settings?.goalGroupSorting?.lifeArea?.sorting?.[id] ?? [],
              allGoals: goals,
            },
          ),
        })),
        // other group for life areas as wel
        {
          id: lifeAreaOtherID,
          items: getSortedGoals(
            activeGoals.filter(({ lifeAreaId }) => !lifeAreaId),
            {
              mode: [user?.settings?.goalSortingMode].filter(
                Boolean,
              ) as GoalSortingMode[],
              order:
                user?.settings?.goalGroupSorting?.lifeArea?.sorting?.['null'] ??
                [],
              allGoals: goals,
            },
          ),
        },
      ].filter(({ items }) => !!items.length),
    [
      activeGoals,
      goals,
      lifeAreas,
      user?.settings?.goalGroupSorting?.lifeArea?.sorting,
      user?.settings?.goalSortingMode,
      user?.settings?.lifeAreaSorting,
    ],
  );

  return {
    sections,
    goals: activeGoals,
  };
};
